import React, { useState, useEffect } from "react";
import {
  Box,
  Drawer,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import CustomText, { textStyles } from "../../../../components/CustomText";
import profilecross from "../../../../assets/profilecross.svg";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { decryptToken } from "../../../../Utlis/Encryption";
import { BASE_URL } from "../../../../components/BaseUrl";
import CustomButton from "../../../../components/CustomButton";
 import valid from "../../../../assets/Attendance/valid.svg";
 import invalid from "../../../../assets/Attendance/validate-svgrepo-com.svg";
 import MarkValidPunchModal from "./ValidModel";
 import CloseIcon from "@mui/icons-material/Close";
 import maparrow from "../../../../assets/maparrow.svg";
import StatusDropdown from "../../../../components/StatusDropdown";
import dummy from "../../../../assets/dummy.png"
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import { convertUTCToLocalDateTime } from "../../../../components/dateTimeUtils";
import {
  getGoogleMapsPinIconForStatus,
  getStatusBadgeBackgroundColor,
} from "../../../../constants/statusPalette";

const UNVALIDATED_PUNCH_STATUS_LABEL_MAP = {
  Validated: "unvalidatedPunches.statusLabels.validatedPunch",
  Invalid: "unvalidatedPunches.statusLabels.unvalidatedPunch",
  Pending: "unvalidatedPunches.statusLabels.pendingPunch",
  "Half Day": "unvalidatedPunches.statusLabels.halfDayLeave",
  HALF_DAY: "unvalidatedPunches.statusLabels.halfDayLeave",
  "half day": "unvalidatedPunches.statusLabels.halfDayLeave",
  "HALF DAY": "unvalidatedPunches.statusLabels.halfDayLeave",
  halfDay: "unvalidatedPunches.statusLabels.halfDayLeave",
  halfday: "unvalidatedPunches.statusLabels.halfDayLeave",
};

const PUNCH_OVERALL_STATUS_LABEL_MAP = {
  Validated: "unvalidatedPunches.statusLabels.validPunch",
  Invalid: "unvalidatedPunches.statusLabels.invalidPunch",
};

const mapContainerStyle = { width: "100%", height: "100%" };

// ✅ Section Header — status dot matches StatusDropdown badge fill
const SectionHeader = ({ label, icon, sectionicon, statusKey }) => (
  <Box mt={2.5} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    {statusKey ? (
      <Box
        aria-hidden
        sx={{
          width: 20,
          height: 20,
          borderRadius: "4px",
          flexShrink: 0,
          backgroundColor: getStatusBadgeBackgroundColor(statusKey),
        }}
      />
    ) : (
      sectionicon && (
        <img src={sectionicon} width={20} height={20} alt="section icon" />
      )
    )}
    <CustomText
      sx={{
        ...textStyles.h1,
        fontSize: 18,
        fontWeight: 600,
        color: "#000000",
      }}
    >
      {label}
    </CustomText>
    {/* <img src={icon} width={20} height={20} alt="dropdown icon" /> */}
  </Box>
);

// ✅ InfoBox
const InfoBox = ({ children }) => (
  <Box
    mt={2}
    sx={{
      border: "1px solid #091E4224",
      borderRadius: "10px",
      px: 2,
      py: 2,
    }}
  >
    {children}
  </Box>
);


const InfoRow = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: 2,
    }}
  >
    <CustomText sx={{ fontSize: 16, fontWeight: 400, color: "#5E5C5C" }}>
      {label}
    </CustomText>

    <Box sx={{ maxWidth: "60%", textAlign: "right" }}>
      {typeof value === "string" || typeof value === "number" ? (
        <CustomText sx={{ fontSize: 15, fontWeight: 400, color: "#172B4D",textAlign:"right" }}>
          {value || "--"}
        </CustomText>
      ) : (
        value || "--"
      )}
    </Box>
  </Box>
);

export default function DetailsDrawer({ open, onClose, punchId,onStatusChange }) {
  const { t } = useTranslation();
  const token = useSelector((state) => state.auth.token);
  const decryptedToken = token ? decryptToken(token) : null;

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [isValidModalOpen, setIsValidModalOpen] = useState(false);
  const [isFullMapOpen, setIsFullMapOpen] = useState(false);
  const [isMapPopupOpen, setIsMapPopupOpen] = useState(false);
  const handleOpenValidModal = () => setIsValidModalOpen(true);
  const handleCloseValidModal = () => setIsValidModalOpen(false);
  const [modalStatus, setModalStatus] = useState("REJECTED");

const handleOpenModal = (status) => {
  setModalStatus(status);
  setIsValidModalOpen(true);
};

const getPinStatusKey = (actionType) => {
  const normalized = String(actionType || "").trim().toUpperCase();
  if (normalized === "ON_BREAK") return "BREAK_START";
  return normalized || "unvalidated";
};

const getMarkerIcon = (status) => getGoogleMapsPinIconForStatus(status);
  useEffect(() => {
    if (!punchId) return;

    const fetchDetails = async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `${BASE_URL}/company-admins/attendance/unvalidated/${punchId}`,
          {
            headers: {
              "Content-Type": "application/json",
              ...(decryptedToken ? { Authorization: `Bearer ${decryptedToken}` } : {}),
            },
          }
        );
        const json = await res.json();
        console.log("Punch Details:", json);
        setDetails(json.data);
      } catch (e) {
        console.error("Failed to fetch punch details", e);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [punchId, decryptedToken]);

  if (!punchId) return null;

const mapReviewStatusToUI = (reviewStatus) => {
  switch (reviewStatus) {
    case "APPROVED":
      return "Validated";
    case "REJECTED":
      return "Invalid";
    case "PENDING":
      return "Pending";
    default:
      return reviewStatus || "--";
  }
};

const formatPunchDateHeader = (utcValue) => {
  const localValue = convertUTCToLocalDateTime(utcValue, {
    locale: "en-GB",
    formatOptions: { day: "2-digit", month: "short", year: "numeric" },
  });
  return localValue === "—" ? "--" : localValue;
};

const mapPunchStatusToOverallUI = (validation) => {
  const punchStatus = String(validation?.punchStatus || "").trim().toUpperCase();
  if (punchStatus === "VALID") return "Validated";
  if (punchStatus) return "Invalid";
  if (validation?.validationIssues?.length) return "Invalid";
  if (
    validation?.faceMatched === false ||
    validation?.locationValidated === false ||
    validation?.scheduleOnTime === false
  ) {
    return "Invalid";
  }
  return "Validated";
};

const renderStatusDropdown = (currentStatus, labelMap) => (
  <Box sx={{ pointerEvents: "none", width: "fit-content", ml: "auto" }}>
    <StatusDropdown currentStatus={currentStatus} labelMap={labelMap} />
  </Box>
);

const renderValidationBadge = (isValid) => {
  if (typeof isValid !== "boolean") return "--";

  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 78,
        px: 1.25,
        py: 0.35,
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: 600,
        fontFamily: "Poppins, sans-serif",
        color: isValid ? "#216E4E" : "#A12622",
        backgroundColor: isValid ? "#DCF3E8" : "#FDE8E8",
        border: isValid ? "1px solid #9BD3BA" : "1px solid #F4B6B6",
      }}
    >
      {isValid ? t("punchesDetailsDrawer.valid") : t("punchesDetailsDrawer.invalid")}
    </Box>
  );
};

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: "100%", sm: 550 }, borderRadius: 0 } }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#E7EBEE",
          p: 2,
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText sx={{     fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
              fontFamily: "Poppins, sans-serif", }}>
            {details?.occurredAt
              ? formatPunchDateHeader(details.occurredAt)
              : t("unvalidatedPunches.detailsTitle")}
          </CustomText>
        </Box>
      </Box>
      <Divider />

      {/* Body */}
      <Box sx={{ px: 2, py: 2, overflowY: "auto" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", height: 200 }}>
            <CircularProgress />
          </Box>
        ) : details ? (
          <>
            {/* Punch Info */}
     <InfoBox>
  <InfoRow
    label={t("common.workerName")}
    value={details.worker?.fullName}
  />
  <InfoRow
    label={t("common.email")}
    value={details.worker?.email}
  />
  <InfoRow
    label={t("punchesDetailsDrawer.validationStatus")}
    value={renderStatusDropdown(
      mapPunchStatusToOverallUI(details.validation),
      PUNCH_OVERALL_STATUS_LABEL_MAP
    )}
  />
  <InfoRow
    label={t("common.punch")}
    value={renderStatusDropdown(getPinStatusKey(details.actionType))}
  />
</InfoBox>



    <SectionHeader label= {t("punchesDetailsDrawer.audit")}  />
<InfoBox>
  <InfoRow
    label={t("attendanceDrawer.scheduleMatch")}
    value={renderValidationBadge(details.validation?.scheduleOnTime)}
  />
  <InfoRow
    label= {t("punchesDetailsDrawer.reviewStatus")}
  value={renderStatusDropdown(
    mapReviewStatusToUI(details.validation?.reviewStatus),
    UNVALIDATED_PUNCH_STATUS_LABEL_MAP
  )}
  />
  <InfoRow
  label={t("punchesDetailsDrawer.reviewNote")}
  value={details.validation?.reviewNote || "--"}
/>

  <InfoRow
    label= {t("punchesDetailsDrawer.faceMatched")}
    value={renderValidationBadge(details.validation?.faceMatched)}
  />
  <InfoRow
    label= {t("punchesDetailsDrawer.locationValidated")}
    value={renderValidationBadge(details.validation?.locationValidated)}
  />
</InfoBox>
          

            {/* Selfie */}
            <SectionHeader label= {t("punchesDetailsDrawer.selfie")}  />
<InfoBox>
  {details.evidence?.selfieUrl ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <CustomText
        sx={{
          fontSize: 16,
          fontWeight: 400,
          color: "#5E5C5C",
        }}
      >
    {t("punchesDetailsDrawer.scanned")}
      </CustomText>
      <Box
                  crossOrigin="anonymous"
        component="img"
        src={details.evidence.selfieUrl}
        alt="Selfie"
          onError={(e) => (e.currentTarget.src = dummy)}
        sx={{
          width: 60,
          height: 60,
          borderRadius: "6px",
          objectFit: "cover",
        }}
      />
    </Box>
  ) : (
    <CustomText> {t("punchesDetailsDrawer.noSelfie")}</CustomText>
  )}
</InfoBox>
         
            
      {details.location?.mapPoint?.lat && details.location?.mapPoint?.lng && (
        <>
        <SectionHeader label= {t("punchesDetailsDrawer.mapView")}  />
<InfoBox>
  <Box sx={{ position: "relative" }}>
    {/* Small Map */}
    <GoogleMap
      mapContainerStyle={{ height: "250px", width: "100%", borderRadius: "8px" }}
      center={{ lat: details.location.mapPoint.lat, lng: details.location.mapPoint.lng }}
      zoom={15}
      options={{
        fullscreenControl: false,
        mapTypeControl: false,
        streetViewControl: false,
      }}
    >
      <Marker
        position={{ lat: details.location.mapPoint.lat, lng: details.location.mapPoint.lng }}
        icon={getMarkerIcon(getPinStatusKey(details.actionType))}
        onClick={() => setIsMapPopupOpen(true)}
      />
      {isMapPopupOpen && (
        <InfoWindow
          position={{ lat: details.location.mapPoint.lat, lng: details.location.mapPoint.lng }}
          onCloseClick={() => setIsMapPopupOpen(false)}
        >
          <Box sx={{ fontFamily: "Poppins, sans-serif" }}>
            {details.location.locationText}
          </Box>
        </InfoWindow>
      )}
    </GoogleMap>

    {/* ✅ Button is outside the map, fixed top-right */}
    <CustomButton
      onClick={() => setIsFullMapOpen(true)}
      size="small"
      sx={{
        position: "absolute",
        top: 12,
        right: 12,
        backgroundColor: "#006EC2",
        color: "white",
        fontSize: "12px",
        padding: "4px 10px",
        borderRadius: "6px",
        zIndex: 1000, // always above the map
      }}
        label={ <> {t("dashboard.viewFullMap")}
     <Box component="img" src={maparrow} alt="view icon"
      sx={{ ml: 1, width: 15, height: 13, objectFit: "contain" }} /> </> }
    />
  </Box>
</InfoBox>
        </>
      )}
          </>
        ) : (
          <CustomText>No details available.</CustomText>
        )}
        


                      <Box
  sx={{
    mt: 3,
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: 1,
  }}
>
  {/* Mark as Invalid */}
  <CustomButton
    onClick={() => handleOpenModal("REJECTED")}
    type="submit"
    label={
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <img src={valid} alt="save" width={16} height={16} />
        {t("attendanceDrawer.markAsInvalid")}
      </Box>
    }
    size="medium"
  />

  {/* Mark as Valid */}
  <CustomButton
    onClick={() => handleOpenModal("APPROVED")}
    type="submit"
    label={
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <img src={invalid} alt="save" width={16} height={16} />
        {t("attendanceDrawer.markAsValid")}
      </Box>
    }
    size="medium"
  />
</Box>

      </Box>
        <MarkValidPunchModal
        open={isValidModalOpen}
        onClose={handleCloseValidModal}
         punchId={punchId}  
           status={modalStatus}
            //  onSuccess={onStatusChange}
              onSuccess={() => {
    onStatusChange();   // refresh list
    onClose();          // CLOSE the drawer
  }} 
      />



         {isFullMapOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 2000,
            backgroundColor: "white",
          }}
        >
       
           <IconButton
               onClick={() => setIsFullMapOpen(false)}
                sx={{ position: "absolute", top: 16, right: 16, zIndex: 2100,backgroundColor:"white",
                  "&:hover": { backgroundColor: "white" }
                 }}
              >
               <CloseIcon sx={{color:"red"}} />
              </IconButton>

          {/* Full map */}
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={{
              lat: details.location.mapPoint.lat,
              lng: details.location.mapPoint.lng,
            }}
            zoom={15}
            options={{
              fullscreenControl: false,
              mapTypeControl: false,
              streetViewControl: false,
            }}
          >
            <Marker
              position={{
                lat: details.location.mapPoint.lat,
                lng: details.location.mapPoint.lng,
              }}
              icon={getMarkerIcon(getPinStatusKey(details.actionType))}
              onClick={() => setIsMapPopupOpen(true)}
            />
            {isMapPopupOpen && (
              <InfoWindow
                position={{
                  lat: details.location.mapPoint.lat,
                  lng: details.location.mapPoint.lng,
                }}
                onCloseClick={() => setIsMapPopupOpen(false)}
              >
                <Box sx={{ fontFamily: "Poppins, sans-serif" }}>
                  {details.location.locationText}
                </Box>
              </InfoWindow>
            )}
          </GoogleMap>
        </Box>
      )}

    </Drawer>
  );
}

